const yaml = require("js-yaml");
const axios = require("axios");
const qs = require("qs");

const { getDebugMode, getEnvironment } = require("../helpers/methods");
const apiUnauthorizedThrown = require("./authentication/api-unauthorized-thrown");
const { setToken, getToken, isTokenExpired, removeToken } = require("./token");
const storage = require("./storage");

const { NAME, SWAGGER_URL, SWAGGER_SERVER_ID } = getEnvironment();

let swagger = null;
let forcedBaseUrl = null;

const beforeQueries = [];
const afterQueries = [];

const beforeQuery = fn => {
	if (beforeQueries.indexOf(fn) === -1) {
		beforeQueries.push(fn);
	}
};

const afterQuery = fn => {
	if (afterQueries.indexOf(fn) === -1) {
		afterQueries.push(fn);
	}
};

function forceBaseUrl(baseUrl) {
	forcedBaseUrl = baseUrl;
}

async function fetchSwagger() {
	const { data } = await axios.get(`${SWAGGER_URL}?ts=${new Date().getTime()}`);

	storage.set("swagger", data, 3600, NAME);

	return data;
}

const getSwagger = async () => {
	try {
		const store = await storage.get("swagger", NAME);

		if (store && store.data) {
			swagger = store.data;
		} else {
			swagger = await fetchSwagger();
		}

		return swagger;
	} catch (error) {
		console.log("[cloudaccounting.js][getSwagger]", error);

		throw new Error(error);
	}
};

const parseSwagger = yamlDoc => {
	try {
		const obj = yaml.safeLoad(yamlDoc);
		let API = {};

		if (!obj) {
			throw new Error("The yaml doc is empty");
		}

		API.BASE_URL = forcedBaseUrl || obj.servers[SWAGGER_SERVER_ID].url;

		for (const [endpoint, path] of Object.entries(obj.paths)) {
			for (const [method, config] of Object.entries(path)) {
				const { operationId, parameters, security } = config;
				const params = [];
				const data = [];

				if (parameters) {
					parameters.forEach(parameter => {
						switch (method) {
							case "get":
								params.push(parameter.name);
								break;

							case "post":
								if (parameter.in === "path") {
									params.push(parameter.name);
								} else {
									data.push(parameter.name);
								}

								break;
						}
					});
				}

				let securityKeys = [];

				if (security && security.length > 0) {
					security.forEach(el => {
						Object.keys(el).forEach(key => {
							securityKeys.push(key);
						});
					});
				}

				API = {
					...API,
					[operationId]: {
						url: endpoint,
						method,
						params,
						data,
						security: securityKeys
					}
				};
			}
		}

		return API;
	} catch (error) {
		console.log("[cloudaccounting.js][parseSwagger]", error);

		throw new Error(error);
	}
};

const getApiUrl = async () => {
	if (swagger === null) {
		swagger = await getSwagger(SWAGGER_URL);
	}

	const obj = yaml.safeLoad(swagger);

	return obj.servers[SWAGGER_SERVER_ID].url;
};

const query = async ({ operationId, token = null, params = {}, data = {} }, forceAuthorizationHeaders = false) => {
	let [method, url] = [null, null];

	try {
		if (typeof operationId === "undefined") {
			throw new Error("The query function require an operationId");
		}

		beforeQueries.forEach(fn => fn());

		if (getDebugMode()) {
			console.log("Running a query for :", {
				operationId,
				token,
				params,
				data
			});
		}

		// Read swagger
		swagger = await getSwagger();
		var API = parseSwagger(swagger);
		var endpoint = API[operationId];

		// Force swagger refresh on undefined method
		if (!endpoint) {
			console.log("Endpoint was not found, we are going to try to fetch the swagger...");
			swagger = await fetchSwagger();
			API = parseSwagger(swagger);
			endpoint = API[operationId];
		}

		// Error on undefined method
		if (!endpoint) {
			throw new Error(`The endpoint for the operationId ${operationId} doesn't exist on the API.`);
		}

		method = endpoint.method;
		url = endpoint.url;

		let _token = token === null ? getToken() : token;

		if (operationId !== "auth.login" && operationId !== "auth.refresh" && endpoint.security && _token) {
			if (isTokenExpired(_token)) {
				try {
					const { access_token } = await query({ operationId: "auth.refresh", token: _token }, true);

					_token = access_token;
					setToken(access_token);
				} catch (error) {
					_token = null;
					removeToken();
				}
			}
		}

		/* if (
			endpoint.params.length > 0 &&
			compareKeys(endpoint.params, params) === false
		) {
			throw new Error(
				`The endpoint for the operationId ${operationId} require the following params to work correctly : ${endpoint.params.toString()}.`
			);
		}

		if (
			endpoint.data.length > 0 &&
			compareKeys(endpoint.data, data) === false
		) {
			throw new Error(
				`The endpoint for the operationId ${operationId} require the following data to work correctly : ${endpoint.data.toString()}.`
			);
		}*/

		if (endpoint.security && endpoint.security.length > 0 && endpoint.security.includes("api_key") && !_token) {
			afterQueries.forEach(fn => fn());

			throw new Error(`The endpoint for the operationId ${operationId} require a token`);
		}

		// Replace the variable in the url with the provided parameters
		let urlWithParams = url;

		for (const [key, value] of Object.entries(params)) {
			urlWithParams = urlWithParams.replace(`{${key}}`, value);
		}

		const response = await axios({
			method: Object.keys(data).length > 0 ? "post" : method,
			url: `${API.BASE_URL}${urlWithParams}`,
			params,
			data,
			headers:
				(endpoint.security && _token) || forceAuthorizationHeaders ? { Authorization: `Bearer ${_token}` } : {},
			paramsSerializer: function (params) {
				return qs.stringify(params, { arrayFormat: "indices", encode: false });
			}
		});

		afterQueries.forEach(fn => fn());

		return response.data;
	} catch (error) {
		console.error("[cloudaccounting.js][query]", `method = ${method} - url = ${url}`, error);

		if (error && error.response && error.response.status === 401) {
			apiUnauthorizedThrown();
		} else {
			afterQueries.forEach(fn => fn());
		}

		return Promise.reject(error);
	}
};

module.exports = cloudaccounting = {
	forceBaseUrl,
	beforeQuery,
	afterQuery,
	query,
	getApiUrl,
	getSwagger
};
